import React, { useState, useEffect } from 'react';
import { X, DollarSign, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const PaymentModal = ({
  enrollmentId,
  category: preSelectedCategory,
  paymentMode: initialMode,
  feeApi,
  formatCurrency,
  onClose,
  onSuccess,
  feeRecord
}) => {
  const [paymentType, setPaymentType] = useState(initialMode || 'multi');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [amount, setAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('UPI');
  const [transactionId, setTransactionId] = useState('');
  const [paymentGateway, setPaymentGateway] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  // Get unpaid categories from fee record
  const unpaidCategories = feeRecord?.breakdown?.filter(cat => {
    const due = cat.amount - cat.paid;
    return due > 0;
  }) || [];

  // Initialize: if preSelectedCategory provided, select it
  useEffect(() => {
    if (preSelectedCategory && paymentType === 'specific') {
      setSelectedCategories([preSelectedCategory._id]);
    }
  }, [preSelectedCategory, paymentType]);

  const paymentMethods = [
    { value: 'Cash', label: 'Cash' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Card', label: 'Debit/Credit Card' },
    { value: 'Net Banking', label: 'Net Banking' },
    { value: 'Cheque', label: 'Cheque' }
  ];

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  // Select/Deselect all categories
  const toggleAllCategories = () => {
    if (selectedCategories.length === unpaidCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(unpaidCategories.map(cat => cat._id));
    }
  };

  // Calculate total due for selected categories
  const selectedTotal = unpaidCategories
    .filter(cat => selectedCategories.includes(cat._id))
    .reduce((sum, cat) => sum + (cat.amount - cat.paid), 0);

  // Check if partial payment is allowed
  const isPartialPaymentAllowed = feeRecord?.feeStructureId?.isPartialPaymentAvailable !== false;

  // Effect to update amount if partial payment is not allowed
  useEffect(() => {
    if (!isPartialPaymentAllowed) {
        if (paymentType === 'multi') {
            setAmount(selectedTotal > 0 ? selectedTotal.toString() : '');
        } else if (paymentType === 'waterfall') {
            // For waterfall, default to total balance or handle as needed. 
            // Assuming for now user must pay full balance if waterfall + no partial
             const totalDue = feeRecord.balance || ((feeRecord.totalAmount || 0) - (feeRecord.totalPaid || 0));
             setAmount(totalDue > 0 ? totalDue.toString() : '');
        }
    }
  }, [selectedTotal, isPartialPaymentAllowed, paymentType, feeRecord]);

  const preventArrowKeys = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const numAmount = parseFloat(amount);
    
    if (!numAmount || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Validation for multi-category
    if (paymentType === 'multi' && selectedCategories.length > 0 && numAmount > selectedTotal) {
      setError(`Amount exceeds selected categories total of ${formatCurrency(selectedTotal)}`);
      return;
    }

    // Validation for Partial Payment Restriction
    if (!isPartialPaymentAllowed) {
        // If not allowed, amount must roughly equal the required total (allow small float diffs if needed, but exact matches preferred)
        let requiredAmount = 0;
        if (paymentType === 'multi') {
            requiredAmount = selectedTotal;
        } else {
             requiredAmount = feeRecord.balance || ((feeRecord.totalAmount || 0) - (feeRecord.totalPaid || 0));
        }

        if (Math.abs(numAmount - requiredAmount) > 0.01) { // Floating point tolerance
             setError(`Partial payment is not allowed. You must pay exactly ${formatCurrency(requiredAmount)}`);
             return;
        }
    }

    setLoading(true);

    try {
      const actualEnrollmentId = feeRecord?.studentEnrollmentId?._id 
        || feeRecord?.studentEnrollmentId?.id 
        || enrollmentId;

      const payload = {
        studentEnrollmentId: actualEnrollmentId,
        amount: numAmount,
        paymentDate: new Date().toISOString(),
        paymentMode: selectedPaymentMethod,
        transactionId: transactionId || undefined,
        paymentGateway: paymentGateway || undefined
      };

      // Add categoryId based on payment type
      if (paymentType === 'multi' && selectedCategories.length > 0) {
        // Multi/single category payment
        payload.categoryId = selectedCategories.length === 1
          ? selectedCategories[0]  // Single string
          : selectedCategories;     // Array
      }
      // If paymentType === 'waterfall' or no categories selected, don't add categoryId

      const response = await feeApi.recordPayment(payload);

      if (response?.success) {
        let categoryNames = '';
        if (paymentType === 'waterfall') {
            categoryNames = 'Waterfall Payment';
        } else {
            const names = unpaidCategories
                .filter(cat => selectedCategories.includes(cat._id))
                .map(cat => cat.categoryName);
            categoryNames = names.join(', ');
            
            // Fallback if name not found but ID exists (shouldn't happen if logic is correct)
            if (!categoryNames && selectedCategories.length > 0) {
                 categoryNames = selectedCategories.length === 1 ? 'Selected Category' : `${selectedCategories.length} Categories`;
            }
        }

        setSuccessData({
            amount: numAmount,
            mode: categoryNames,
            transactionId: response.transactionId || transactionId
        });
        
        toast.success('Payment recorded successfully');
        
        // Give user time to read success message
        setTimeout(() => {
            onSuccess();
        }, 4000);
      } else {
        setError(response?.message || 'Failed to record payment');
        toast.error(response?.message || 'Failed to record payment');
      }
    } catch (err) {
      const errorMsg = err?.payload?.message || err?.message || 'Payment failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (successData) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full p-8 text-center animate-in fade-in zoom-in duration-300">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
                <div className="space-y-1 text-sm text-gray-600 mb-6">
                    <p>Amount Paid: <span className="font-semibold text-gray-900">{formatCurrency(successData.amount)}</span></p>
                    <p>For: <span className="font-semibold text-gray-900">{successData.mode}</span></p>
                    {successData.transactionId && (
                        <p className="text-xs text-gray-500 mt-2">Ref: {successData.transactionId}</p>
                    )}
                </div>
                <div className="text-xs text-gray-400">
                    Closing in a few seconds...
                </div>
            </div>
        </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Make Payment</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Select categories or use waterfall payment
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Payment Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Payment Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPaymentType('multi')}
                className={`p-2 border rounded text-xs font-medium transition-all ${
                  paymentType === 'multi'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Select Categories
              </button>
              <button
                type="button"
                onClick={() => {
                  setPaymentType('waterfall');
                  setSelectedCategories([]);
                }}
                className={`p-2 border rounded text-xs font-medium transition-all ${
                  paymentType === 'waterfall'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Auto-Distribute
              </button>
            </div>
          </div>

          {/* Category Selection - Multi Mode */}
          {paymentType === 'multi' && (
            <div className="border border-gray-200 rounded p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">
                  Select Categories to Pay
                </label>
                <button
                  type="button"
                  onClick={toggleAllCategories}
                  className="text-[10px] font-medium hover:text-indigo-700 hover:underline"
                  style={{ color: 'var(--primary-color, #4f46e5)' }}
                >
                  {selectedCategories.length === unpaidCategories.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {unpaidCategories.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-3">No unpaid categories available</p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {unpaidCategories.map((cat, idx) => {
                    const due = cat.amount - cat.paid;
                    const isSelected = selectedCategories.includes(cat._id);
                    
                    return (
                      <label
                        key={idx}
                        className={`flex items-center p-2 bg-white border rounded cursor-pointer transition-all ${
                          isSelected
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleCategory(cat._id)}
                          className="w-4 h-4 border-gray-300 rounded"
                          style={{ accentColor: 'var(--primary-color, #4f46e5)' }}
                        />
                        <div className="flex-1 ml-2.5">
                          <span className={`text-xs font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
                            {cat.categoryName}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-gray-700">
                          {formatCurrency(due)} due
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Selection Summary */}
              <div className="mt-2 p-2 bg-white border border-gray-200 rounded text-center">
                {selectedCategories.length > 0 ? (
                  <div className="text-xs">
                    <span className="font-medium text-green-600">
                      ✅ {selectedCategories.length} {selectedCategories.length === 1 ? 'category' : 'categories'} selected
                    </span>
                    <br />
                    <span className="text-gray-600">
                      Total Due: <strong className="text-gray-900">{formatCurrency(selectedTotal)}</strong>
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-500">
                    ⚪ No categories selected (will use waterfall payment)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Amount to Pay <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">
                ₹
              </span>
              <input
                type="number"
                value={amount}
                onKeyDown={preventArrowKeys}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => setAmount(e.target.value)}
                className={`w-full pl-8 pr-3 py-2 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                  !isPartialPaymentAllowed ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200' : 'border-gray-300'
                }`}
                placeholder="0"
                required
                min="1"
                step="0.01"
                readOnly={!isPartialPaymentAllowed}
              />
            </div>
            {!isPartialPaymentAllowed && (
              <p className="text-[10px] text-red-500 mt-1 font-medium">
                Partial payments are not allowed for this fee structure. You must pay the full amount.
              </p>
            )}
            {paymentType === 'multi' && selectedCategories.length > 0 && isPartialPaymentAllowed && (
              <p className="text-[10px] text-gray-500 mt-1">
                Selected categories total: {formatCurrency(selectedTotal)}
              </p>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Payment Mode <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors appearance-none bg-white"
                required
              >
                {paymentMethods.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Transaction ID */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Transaction ID <span className="text-gray-400 text-[10px]">(Optional)</span>
            </label>
            <input
              type="text"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="TXN123456"
            />
          </div>

          {/* Payment Gateway */}
          {(selectedPaymentMethod === 'UPI' || selectedPaymentMethod === 'Card') && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Payment Gateway <span className="text-gray-400 text-[10px]">(Optional)</span>
              </label>
              <select
                value={paymentGateway}
                onChange={(e) => setPaymentGateway(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors appearance-none bg-white"
              >
                <option value="">Select Gateway</option>
                <option value="Razorpay">Razorpay</option>
                <option value="Paytm">Paytm</option>
                <option value="PhonePe">PhonePe</option>
                <option value="GooglePay">Google Pay</option>
                <option value="Other">Other</option>
              </select>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount}
              className="btn-primary flex-1 text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4" />
                  Make Payment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
